import { useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { DEFAULT_PIPELINE_STAGES } from '@trinco/shared';
import type { PipelineStage } from '@trinco/shared';

export function SettingsPipelinePage() {
  const [stages, setStages] = useState<PipelineStage[]>(DEFAULT_PIPELINE_STAGES);
  const [saving, setSaving] = useState(false);

  const addStage = () => {
    const newStage: PipelineStage = {
      id: `stage_${Date.now()}`,
      name: 'New Stage',
      slug: `new-stage-${Date.now()}`,
      position: stages.length,
      probability: 50,
      isWon: false,
      isLost: false,
      color: '#6B7280',
    };
    setStages([...stages, newStage]);
  };

  const removeStage = (id: string) => {
    setStages(stages.filter((s) => s.id !== id));
  };

  const updateStage = (id: string, field: keyof PipelineStage, value: any) => {
    setStages(
      stages.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    // Will be wired to trpc.settings.updatePipelineStages
    await new Promise((r) => setTimeout(r, 500));
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Pipeline Stages</h1>
        <p className="text-muted-foreground">
          Configure the stages in your sales pipeline.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Stages</CardTitle>
          <CardDescription>
            Drag to reorder. Set probability weights for forecasting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {stages.map((stage) => (
            <div
              key={stage.id}
              className="flex items-center gap-3 rounded-lg border p-3"
            >
              <GripVertical className="h-4 w-4 cursor-grab text-muted-foreground" />

              <Input
                value={stage.name}
                onChange={(e) => updateStage(stage.id, 'name', e.target.value)}
                className="w-40"
              />

              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={stage.probability}
                  onChange={(e) =>
                    updateStage(stage.id, 'probability', parseInt(e.target.value) || 0)
                  }
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>

              <input
                type="color"
                value={stage.color ?? '#6B7280'}
                onChange={(e) => updateStage(stage.id, 'color', e.target.value)}
                className="h-8 w-8 cursor-pointer rounded border-0"
              />

              <div className="flex items-center gap-2">
                <Switch
                  checked={stage.isWon}
                  onCheckedChange={(v) => updateStage(stage.id, 'isWon', v)}
                />
                <Label className="text-xs">Won</Label>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={stage.isLost}
                  onCheckedChange={(v) => updateStage(stage.id, 'isLost', v)}
                />
                <Label className="text-xs">Lost</Label>
              </div>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeStage(stage.id)}
                className="ml-auto text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}

          <Button variant="outline" onClick={addStage} className="w-full">
            <Plus className="mr-2 h-4 w-4" />
            Add stage
          </Button>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save pipeline'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
